import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin")({
  head: () => ({ meta: [{ title: "Admin — Submissions" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminPage,
});

type Tab = "customer_submissions" | "shop_orders" | "custom_quote_requests" | "fabric_quote_requests";

const TABS: { id: Tab; label: string }[] = [
  { id: "customer_submissions", label: "All Submissions" },
  { id: "shop_orders", label: "Shop Orders" },
  { id: "custom_quote_requests", label: "Custom Quotes" },
  { id: "fabric_quote_requests", label: "Fabric Quotes" },
];

function AdminPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("customer_submissions");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; field: string; value: string } | null>(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignValue, setAssignValue] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyFrequency, setNotifyFrequency] = useState<"immediate" | "daily_digest">("immediate");
  const [notifyDigestHour, setNotifyDigestHour] = useState<number>(13);
  const [notifyDigestTz, setNotifyDigestTz] = useState<string>(
    typeof Intl !== "undefined" ? (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") : "UTC",
  );
  const [notifyAllowedSources, setNotifyAllowedSources] = useState<string[]>([
    "shop_order", "custom_quote", "fabric_quote",
  ]);
  const [notifyKeyword, setNotifyKeyword] = useState<string>("");
  const [notifyStatus, setNotifyStatus] = useState<string>("");
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsDigestEnabled, setSmsDigestEnabled] = useState(false);
  const [smsRecipientPhone, setSmsRecipientPhone] = useState("");
  const [smsFromPhone, setSmsFromPhone] = useState("");
  const [sourceRecipients, setSourceRecipients] = useState<Record<string, string>>({
    shop_order: "", custom_quote: "", fabric_quote: "",
  });
  const [accessMode, setAccessMode] = useState<"first_user_admin" | "manual_approval">("first_user_admin");
  const [accessStatus, setAccessStatus] = useState<string>("");

  useEffect(() => {
    if (!isAdmin) return;
    (supabase as any).from("notification_settings").select("*").eq("id", 1).maybeSingle()
      .then(({ data }: any) => {
        if (data) {
          setNotifyEmail(data.recipient_email ?? "");
          setNotifyEnabled(!!data.enabled);
          setNotifyFrequency(data.frequency === "daily_digest" ? "daily_digest" : "immediate");
          setNotifyDigestHour(typeof data.digest_hour_utc === "number" ? data.digest_hour_utc : 13);
          if (typeof (data as any).digest_timezone === "string" && (data as any).digest_timezone) {
            setNotifyDigestTz((data as any).digest_timezone);
          }
          if (Array.isArray(data.allowed_sources)) setNotifyAllowedSources(data.allowed_sources);
          setNotifyKeyword(data.keyword_filter ?? "");
          setSmsEnabled(!!data.sms_enabled);
          setSmsDigestEnabled(!!(data as any).sms_digest_enabled);
          setSmsRecipientPhone(data.sms_recipient_phone ?? "");
          setSmsFromPhone(data.sms_from_phone ?? "");
          if (data.source_recipients && typeof data.source_recipients === "object") {
            const sr = data.source_recipients as Record<string, string | string[]>;
            setSourceRecipients({
              shop_order: Array.isArray(sr.shop_order) ? sr.shop_order.join(", ") : (sr.shop_order ?? ""),
              custom_quote: Array.isArray(sr.custom_quote) ? sr.custom_quote.join(", ") : (sr.custom_quote ?? ""),
              fabric_quote: Array.isArray(sr.fabric_quote) ? sr.fabric_quote.join(", ") : (sr.fabric_quote ?? ""),
            });
          }
        }
      });
    (supabase as any).from("admin_access_settings").select("mode").eq("id", 1).maybeSingle()
      .then(({ data }: any) => { if (data?.mode) setAccessMode(data.mode); });
  }, [isAdmin]);

  const toggleSource = (s: string) => {
    setNotifyAllowedSources((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const saveNotify = async () => {
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const splitEmails = (v: string) =>
      v.split(/[,\s;]+/).map((e) => e.trim()).filter(Boolean);
    const validateList = (label: string, raw: string): { valid: string[]; error?: string } => {
      const parts = splitEmails(raw);
      const invalid = parts.filter((e) => !EMAIL_RE.test(e));
      if (invalid.length) return { valid: [], error: `${label}: invalid email${invalid.length > 1 ? "s" : ""} — ${invalid.join(", ")}` };
      return { valid: parts };
    };

    const def = validateList("Default recipients", notifyEmail);
    if (def.error) { setNotifyStatus(`Error: ${def.error}`); setTimeout(() => setNotifyStatus(""), 4000); return; }

    const sr: Record<string, string[]> = {};
    for (const k of Object.keys(sourceRecipients)) {
      const res = validateList(`Routing for ${k}`, sourceRecipients[k]);
      if (res.error) { setNotifyStatus(`Error: ${res.error}`); setTimeout(() => setNotifyStatus(""), 4000); return; }
      if (res.valid.length) sr[k] = res.valid;
    }

    if (notifyEnabled && def.valid.length === 0) {
      setNotifyStatus("Error: At least one default recipient email is required when alerts are enabled");
      setTimeout(() => setNotifyStatus(""), 4000);
      return;
    }

    setNotifyStatus("Saving…");
    const { error } = await (supabase as any).from("notification_settings")
      .update({
        recipient_email: def.valid.join(", ") || null,
        enabled: notifyEnabled,
        frequency: notifyFrequency,
        digest_hour_utc: notifyDigestHour,
        digest_timezone: notifyDigestTz || "UTC",
        allowed_sources: notifyAllowedSources,
        keyword_filter: notifyKeyword.trim() || null,
        sms_enabled: smsEnabled,
        sms_digest_enabled: smsDigestEnabled,
        sms_recipient_phone: smsRecipientPhone.trim() || null,
        sms_from_phone: smsFromPhone.trim() || null,
        source_recipients: sr,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1);
    setNotifyStatus(error ? `Error: ${error.message}` : "Saved");
    setTimeout(() => setNotifyStatus(""), 2500);
  };

  const saveAccessMode = async (mode: "first_user_admin" | "manual_approval") => {
    setAccessMode(mode);
    setAccessStatus("Saving…");
    const { error } = await (supabase as any).from("admin_access_settings")
      .update({ mode, updated_at: new Date().toISOString() }).eq("id", 1);
    setAccessStatus(error ? `Error: ${error.message}` : "Saved");
    setTimeout(() => setAccessStatus(""), 2500);
  };
  const [historyFor, setHistoryFor] = useState<{ id: string; label: string } | null>(null);
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = useCallback(async (row: any) => {
    const submissionId = tab === "customer_submissions" ? row.id : row.id;
    const label = row.customer_name || row.customer_email || row.id;
    setHistoryFor({ id: submissionId, label });
    setHistoryLoading(true);
    setHistoryRows([]);
    // Audit log is keyed by customer_submissions.id. For source tables, look up by source_id.
    const query = tab === "customer_submissions"
      ? (supabase as any).from("submission_audit_log").select("*").eq("submission_id", row.id)
      : (supabase as any).from("customer_submissions").select("id").eq("source_id", row.id).maybeSingle();
    if (tab === "customer_submissions") {
      const { data } = await query.order("created_at", { ascending: false });
      setHistoryRows(data ?? []);
    } else {
      const { data: cs } = await query;
      if (cs?.id) {
        const { data } = await (supabase as any)
          .from("submission_audit_log").select("*")
          .eq("submission_id", cs.id).order("created_at", { ascending: false });
        setHistoryRows(data ?? []);
      }
    }
    setHistoryLoading(false);
  }, [tab]);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      if (!data.session) { navigate({ to: "/auth" }); return; }
      setUserEmail(data.session.user.email ?? null);
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!roles);
      setAuthChecked(true);
    });
    return () => { active = false; };
  }, [navigate]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true); setError(null);
    const { data, error } = await supabase.from(tab).select("*").order("created_at", { ascending: false }).limit(1000);
    if (error) setError(error.message);
    let result: any[] = data ?? [];
    if (tab === "custom_quote_requests" && result.length > 0) {
      const ids = result.map((r) => r.id);
      const { data: atts } = await (supabase as any)
        .from("custom_quote_attachments")
        .select("quote_request_id, file_url, file_name, content_type, sort_order")
        .in("quote_request_id", ids)
        .order("sort_order", { ascending: true });
      const byId = new Map<string, any[]>();
      for (const a of atts ?? []) {
        const arr = byId.get(a.quote_request_id) ?? [];
        arr.push(a);
        byId.set(a.quote_request_id, arr);
      }
      result = result.map((r) => ({ ...r, attachments: byId.get(r.id) ?? [] }));
    }
    if (tab === "customer_submissions" && result.length > 0) {
      const ids = result.map((r) => r.id);
      const { data: queue } = await (supabase as any)
        .from("submission_notification_queue")
        .select("submission_id, status, attempts, last_error, last_attempt_at, next_attempt_at")
        .in("submission_id", ids);
      const qById = new Map<string, any>();
      for (const q of queue ?? []) qById.set(q.submission_id, q);
      result = result.map((r) => {
        const q = qById.get(r.id);
        return {
          ...r,
          notify_status: q?.status ?? "—",
          notify_attempts: q?.attempts ?? 0,
          notify_last_error: q?.last_error ?? null,
          notify_last_attempt_at: q?.last_attempt_at ?? null,
          notify_next_attempt_at: q?.next_attempt_at ?? null,
        };
      });
    }
    setRows(result);
    setLoading(false);
  }, [tab, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Realtime: refresh the current tab whenever a relevant row changes
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel(`admin-${tab}`)
      .on("postgres_changes", { event: "*", schema: "public", table: tab }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tab, isAdmin, load]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this entry permanently?")) return;
    const { error } = await supabase.from(tab).delete().eq("id", id);
    if (error) alert(error.message);
    else load();
  };

  const retryNotification = async (id: string) => {
    try {
      // Reset queue row so the cron worker picks it up immediately
      await (supabase as any)
        .from("submission_notification_queue")
        .upsert({ submission_id: id, status: "pending", attempts: 0, next_attempt_at: new Date().toISOString(), last_error: null }, { onConflict: "submission_id" });
      const res = await fetch("/api/public/hooks/notify-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submission_id: id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!json?.ok && !json?.queued) alert(`Retry queued but webhook reported: ${JSON.stringify(json)}`);
      load();
    } catch (e: any) {
      alert(`Retry failed: ${e?.message ?? e}`);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { error } = await (supabase.from(tab) as any).update({ [editing.field]: editing.value }).eq("id", editing.id);
    if (error) alert(error.message);
    setEditing(null);
    load();
  };

  // Filter + search
  const filtered = useMemo(() => {
    let r = rows;
    if (tab === "customer_submissions" && sourceFilter !== "all") {
      r = r.filter((row) => row.source === sourceFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return r;
    return r.filter((row) =>
      Object.values(row).some((v) => {
        if (v === null || v === undefined) return false;
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        return s.toLowerCase().includes(q);
      }),
    );
  }, [rows, search, tab, sourceFilter]);

  // Clear selection when switching tabs or filters
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const selectedRows = useMemo(
    () => filtered.filter((r) => selected.has(r.id)),
    [filtered, selected],
  );

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((r) => r.id)));
    }
  };

  const exportCsv = (rowsToExport = selectedRows.length > 0 ? selectedRows : filtered) => {
    if (!rowsToExport.length) return;
    const keys = Object.keys(rowsToExport[0]);
    const escape = (v: any) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [keys.join(","), ...rowsToExport.map((r) => keys.map((k) => escape(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${tab}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const supportsLeadFields = tab === "customer_submissions";

  const bulkUpdate = async (patch: Record<string, any>, label: string) => {
    if (selected.size === 0) return;
    setBulkStatus(`${label}…`);
    const ids = Array.from(selected);
    const { error } = await (supabase.from(tab) as any).update(patch).in("id", ids);
    if (error) { setBulkStatus(`Error: ${error.message}`); return; }
    setBulkStatus(`${label} ${ids.length}`);
    setSelected(new Set());
    await load();
    setTimeout(() => setBulkStatus(""), 2500);
  };

  const bulkMarkContacted = () =>
    bulkUpdate({ status: "contacted", contacted_at: new Date().toISOString() }, "Marked contacted");
  const bulkSetStatus = (status: string) =>
    bulkUpdate({ status }, `Set ${status}`);
  const bulkAssign = async () => {
    const value = assignValue.trim();
    if (!value) return;
    await bulkUpdate({ assigned_to: value }, `Assigned to ${value}`);
    setAssignOpen(false);
    setAssignValue("");
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} selected entries permanently?`)) return;
    const ids = Array.from(selected);
    const { error } = await supabase.from(tab).delete().in("id", ids);
    if (error) { alert(error.message); return; }
    setSelected(new Set());
    load();
  };

  if (!authChecked) return <div className="p-10 text-center">Loading…</div>;

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center border-4 border-navy-deep bg-white p-8">
          <h1 className="font-display text-2xl font-bold uppercase text-navy-deep">No admin access</h1>
          <p className="mt-2 text-sm text-navy-deep/70">
            Signed in as <strong>{userEmail}</strong>. An existing admin must grant you admin access from the backend.
          </p>
          <button onClick={signOut} className="mt-4 bg-red px-4 py-2 font-display text-xs uppercase text-white">Sign out</button>
        </div>
      </div>
    );
  }

  const sources = tab === "customer_submissions"
    ? Array.from(new Set(rows.map((r) => r.source).filter(Boolean)))
    : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-4 border-navy-deep bg-white px-4 py-4 sm:px-8 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <Link to="/" className="text-xs uppercase tracking-wider text-navy-deep/60 hover:text-red">← Site</Link>
          <h1 className="font-display text-2xl font-bold uppercase text-navy-deep">Customer Submissions</h1>
          <div className="flex flex-wrap gap-3">
            <Link to={"/admin/alerts" as any} className="text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">Alert email log →</Link>
            <Link to={"/admin/searches" as any} className="text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">Site searches →</Link>
            <Link to={"/admin/settings" as any} className="text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">Site settings →</Link>
            <Link to={"/admin/blog" as any} className="text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">Blog posts →</Link>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-navy-deep/70">{userEmail}</span>
          <button onClick={signOut} className="border-2 border-navy-deep px-3 py-1 font-display text-xs uppercase hover:bg-navy-deep hover:text-white">Sign out</button>
        </div>
      </header>

      <div className="px-4 py-6 sm:px-8">
        <div className="mb-6 border-2 border-navy-deep bg-white p-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep mb-2">Email alerts on new submissions</h2>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="notify-email" className="sr-only">Alert recipient email(s)</label>
            <input id="notify-email" type="text" value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="alerts@yourdomain.com, ops@yourdomain.com"
              className="flex-1 min-w-[240px] border-2 border-navy-deep bg-white px-3 py-2 text-sm" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notifyEnabled} onChange={(e) => setNotifyEnabled(e.target.checked)} />
              Enabled
            </label>
          </div>
          <p className="mt-2 text-[11px] text-navy-deep/60">Separate multiple addresses with commas. Each recipient gets their own copy with an individual unsubscribe link.</p>
          <fieldset className="mt-3 space-y-2 text-sm" disabled={!notifyEnabled}>
            <legend className="font-display text-xs uppercase tracking-wider text-navy-deep/80 mb-1">Frequency</legend>
            <label className="flex items-start gap-2">
              <input type="radio" name="notify-freq" className="mt-1"
                checked={notifyFrequency === "immediate"}
                onChange={() => setNotifyFrequency("immediate")} />
              <span><strong>Immediate</strong> — send an email each time a submission arrives.</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="radio" name="notify-freq" className="mt-1"
                checked={notifyFrequency === "daily_digest"}
                onChange={() => setNotifyFrequency("daily_digest")} />
              <span className="flex-1 flex flex-wrap items-center gap-2">
                <strong>Daily digest</strong> — one summary email per day at
                <select value={notifyDigestHour}
                  onChange={(e) => setNotifyDigestHour(parseInt(e.target.value, 10))}
                  className="border-2 border-navy-deep bg-white px-2 py-0.5 text-sm">
                  {Array.from({ length: 24 }).map((_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                  ))}
                </select>
                <select value={notifyDigestTz}
                  onChange={(e) => setNotifyDigestTz(e.target.value)}
                  className="border-2 border-navy-deep bg-white px-2 py-0.5 text-sm">
                  {(() => {
                    const browserTz = typeof Intl !== "undefined" ? (Intl.DateTimeFormat().resolvedOptions().timeZone || "") : "";
                    const common = [
                      "UTC",
                      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                      "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
                      "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
                      "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo",
                      "Australia/Sydney",
                    ];
                    const set = new Set<string>();
                    const list: string[] = [];
                    for (const tz of [browserTz, notifyDigestTz, ...common]) {
                      if (tz && !set.has(tz)) { set.add(tz); list.push(tz); }
                    }
                    return list.map((tz) => <option key={tz} value={tz}>{tz}</option>);
                  })()}
                </select>
              </span>
            </label>
          </fieldset>

          <fieldset className="mt-4 space-y-2 text-sm" disabled={!notifyEnabled}>
            <legend className="font-display text-xs uppercase tracking-wider text-navy-deep/80 mb-1">Filters</legend>
            <div className="flex flex-wrap gap-3">
              {[
                { id: "shop_order", label: "Shop orders" },
                { id: "custom_quote", label: "Custom quotes" },
                { id: "fabric_quote", label: "Fabric quotes" },
              ].map((s) => (
                <label key={s.id} className="flex items-center gap-2">
                  <input type="checkbox"
                    checked={notifyAllowedSources.includes(s.id)}
                    onChange={() => toggleSource(s.id)} />
                  {s.label}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="notify-keyword" className="font-display text-xs uppercase tracking-wider text-navy-deep/70">Keyword</label>
              <input id="notify-keyword" type="text" value={notifyKeyword}
                onChange={(e) => setNotifyKeyword(e.target.value)}
                placeholder="e.g. Austin, sunbrella, repair"
                className="flex-1 min-w-[200px] border-2 border-navy-deep bg-white px-3 py-2 text-sm" />
            </div>
            <p className="text-[11px] text-navy-deep/60">Only notify for submissions whose source is checked above. If a keyword is set, the product, notes, address, name, or email must contain it (case-insensitive). Leave keyword blank for no keyword filter.</p>
          </fieldset>

          <fieldset className="mt-4 space-y-2 text-sm">
            <legend className="font-display text-xs uppercase tracking-wider text-navy-deep/80 mb-1">SMS alerts</legend>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={smsEnabled} onChange={(e) => setSmsEnabled(e.target.checked)} />
              Also send an SMS for each new submission (immediate only; respects the source &amp; keyword filters above)
            </label>
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[220px]">
                <label htmlFor="sms-recipient" className="font-display text-[11px] uppercase tracking-wider text-navy-deep/70">Recipient phone</label>
                <input id="sms-recipient" type="tel" value={smsRecipientPhone}
                  onChange={(e) => setSmsRecipientPhone(e.target.value)}
                  disabled={!smsEnabled}
                  placeholder="+15125551234"
                  className="mt-1 w-full border-2 border-navy-deep bg-white px-3 py-2 text-sm disabled:opacity-50" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <label htmlFor="sms-from" className="font-display text-[11px] uppercase tracking-wider text-navy-deep/70">Twilio sender phone</label>
                <input id="sms-from" type="tel" value={smsFromPhone}
                  onChange={(e) => setSmsFromPhone(e.target.value)}
                  disabled={!smsEnabled}
                  placeholder="+18885550199"
                  className="mt-1 w-full border-2 border-navy-deep bg-white px-3 py-2 text-sm disabled:opacity-50" />
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={smsDigestEnabled} disabled={!smsEnabled}
                onChange={(e) => setSmsDigestEnabled(e.target.checked)} />
              Also send a daily SMS digest summarizing the last 24 hours (uses the same digest hour &amp; timezone as email)
            </label>
            <p className="text-[11px] text-navy-deep/60">Use E.164 format (e.g. +15125551234). The per-submission SMS sends immediately. The daily digest SMS sends once per day at the digest hour above and contains a one-line summary (count by source).</p>
          </fieldset>

          <fieldset className="mt-4 space-y-2 text-sm">
            <legend className="font-display text-xs uppercase tracking-wider text-navy-deep/80 mb-1">Routing by source</legend>
            <p className="text-[11px] text-navy-deep/60">Optional. Send each source's alerts to specific recipients instead of the default email above. Separate multiple emails with commas. Leave blank to fall back to the default recipient.</p>
            {[
              { id: "shop_order", label: "Shop orders" },
              { id: "custom_quote", label: "Custom quotes" },
              { id: "fabric_quote", label: "Fabric quotes" },
            ].map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2">
                <label htmlFor={`route-${s.id}`} className="font-display text-[11px] uppercase tracking-wider text-navy-deep/70 w-32">{s.label}</label>
                <input id={`route-${s.id}`} type="text"
                  value={sourceRecipients[s.id] ?? ""}
                  onChange={(e) => setSourceRecipients((p) => ({ ...p, [s.id]: e.target.value }))}
                  placeholder="ops@yourdomain.com, manager@yourdomain.com"
                  className="flex-1 min-w-[240px] border-2 border-navy-deep bg-white px-3 py-2 text-sm" />
              </div>
            ))}
          </fieldset>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={saveNotify} className="border-2 border-navy-deep bg-navy-deep px-3 py-2 font-display text-xs uppercase text-white hover:bg-red">Save</button>
            {notifyStatus && <span className="text-xs text-navy-deep/70">{notifyStatus}</span>}
          </div>
          <p className="mt-2 text-[11px] text-navy-deep/60">Immediate sends one email per submission. Daily digest groups the previous 24 hours into a single email at the chosen UTC hour.</p>
        </div>

        <div className="mb-6 border-2 border-navy-deep bg-white p-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep mb-2">Admin access mode</h2>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2">
              <input type="radio" name="access-mode" className="mt-1" checked={accessMode === "first_user_admin"}
                onChange={() => saveAccessMode("first_user_admin")} />
              <span><strong>First signup becomes admin</strong> — the very first account to sign up is automatically granted admin. Subsequent signups get no access until you grant it.</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="radio" name="access-mode" className="mt-1" checked={accessMode === "manual_approval"}
                onChange={() => saveAccessMode("manual_approval")} />
              <span><strong>Manual approval</strong> — every new signup must be granted admin manually.</span>
            </label>
            {accessStatus && <p className="text-xs text-navy-deep/70">{accessStatus}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setSourceFilter("all"); setSearch(""); }}
              className={`px-4 py-2 font-display text-xs uppercase tracking-wider border-2 border-navy-deep ${tab === t.id ? "bg-navy-deep text-white" : "bg-white text-navy-deep hover:bg-cream"}`}>
              {t.label}
            </button>
          ))}
          <button onClick={load} className="ml-auto border-2 border-navy-deep px-3 py-2 font-display text-xs uppercase hover:bg-cream">Refresh</button>
          <button onClick={() => exportCsv()} disabled={!filtered.length} className="border-2 border-navy-deep bg-red px-3 py-2 font-display text-xs uppercase text-white disabled:opacity-50">
            Export CSV {selected.size > 0 && `(${selected.size})`}
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label htmlFor="admin-search" className="sr-only">Search submissions</label>
          <input
            id="admin-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, product, notes…"
            aria-label="Search submissions"
            className="flex-1 min-w-[240px] border-2 border-navy-deep bg-white px-3 py-2 text-sm"
          />
          {tab === "customer_submissions" && sources.length > 0 && (
            <>
              <label htmlFor="src-filter" className="sr-only">Filter by source</label>
              <select
                id="src-filter"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                aria-label="Filter by source form"
                className="border-2 border-navy-deep bg-white px-3 py-2 text-sm"
              >
                <option value="all">All sources</option>
                {sources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </>
          )}
          <span className="text-xs text-navy-deep/60">
            {filtered.length} of {rows.length}
          </span>
        </div>

        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 border-2 border-navy-deep bg-cream px-3 py-2">
            <span className="font-display text-xs uppercase tracking-wider text-navy-deep">
              {selected.size} selected
            </span>
            <button onClick={() => exportCsv()} className="border-2 border-navy-deep bg-white px-2 py-1 text-xs font-display uppercase hover:bg-navy-deep hover:text-white">
              Export
            </button>
            {supportsLeadFields && (
              <>
                <button onClick={bulkMarkContacted} className="border-2 border-navy-deep bg-white px-2 py-1 text-xs font-display uppercase hover:bg-navy-deep hover:text-white">
                  Mark contacted
                </button>
                <label htmlFor="bulk-status" className="sr-only">Set status</label>
                <select
                  id="bulk-status"
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) { bulkSetStatus(e.target.value); e.target.value = ""; } }}
                  className="border-2 border-navy-deep bg-white px-2 py-1 text-xs"
                >
                  <option value="">Set status…</option>
                  <option value="new">new</option>
                  <option value="contacted">contacted</option>
                  <option value="qualified">qualified</option>
                  <option value="won">won</option>
                  <option value="lost">lost</option>
                </select>
                <button onClick={() => setAssignOpen((v) => !v)} className="border-2 border-navy-deep bg-white px-2 py-1 text-xs font-display uppercase hover:bg-navy-deep hover:text-white">
                  Assign…
                </button>
                {assignOpen && (
                  <div className="flex items-center gap-1">
                    <label htmlFor="bulk-assign" className="sr-only">Assign to</label>
                    <input
                      id="bulk-assign"
                      type="text"
                      placeholder="Name or email"
                      value={assignValue}
                      onChange={(e) => setAssignValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") bulkAssign(); }}
                      className="border-2 border-navy-deep bg-white px-2 py-1 text-xs"
                    />
                    <button onClick={bulkAssign} className="bg-red px-2 py-1 text-xs font-display uppercase text-white">Save</button>
                  </div>
                )}
              </>
            )}
            <button onClick={bulkDelete} className="border-2 border-red bg-white px-2 py-1 text-xs font-display uppercase text-red hover:bg-red hover:text-white">
              Delete
            </button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-navy-deep/70 underline">
              Clear
            </button>
            {bulkStatus && <span className="text-xs text-navy-deep/70">{bulkStatus}</span>}
          </div>
        )}

        {error && <p className="text-sm text-red mb-3">{error}</p>}
        {loading ? (
          <p className="text-sm text-navy-deep/70">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-navy-deep/70">{rows.length === 0 ? "No entries yet." : "No matches for that search."}</p>
        ) : (
          <div className="overflow-x-auto border-2 border-navy-deep bg-white">
            <table className="w-full text-xs">
              <thead className="bg-navy-deep text-white">
                <tr>
                  <th className="px-2 py-2 text-left">
                    <label className="sr-only" htmlFor="select-all">Select all</label>
                    <input
                      id="select-all"
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === filtered.length}
                      ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < filtered.length; }}
                      onChange={toggleAll}
                    />
                  </th>
                  {Object.keys(filtered[0]).map((k) => (
                    <th key={k} className="px-2 py-2 text-left font-display uppercase tracking-wider whitespace-nowrap">{k}</th>
                  ))}
                  <th className="px-2 py-2 text-left font-display uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className={`border-t border-navy-deep/20 align-top hover:bg-cream/40 ${selected.has(r.id) ? "bg-cream/60" : ""}`}>
                    <td className="px-2 py-2">
                      <label className="sr-only" htmlFor={`select-${r.id}`}>Select row</label>
                      <input
                        id={`select-${r.id}`}
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleRow(r.id)}
                      />
                    </td>
                    {Object.keys(filtered[0]).map((k) => {
                      const isEditing = !!editing && editing.id === r.id && editing.field === k;
                      const editable = !["id", "created_at", "source", "source_id", "payload", "attachments", "notify_status", "notify_attempts", "notify_last_error", "notify_last_attempt_at", "notify_next_attempt_at"].includes(k);
                      const v = r[k];
                      if (k === "attachments" && Array.isArray(v)) {
                        return (
                          <td key={k} className="px-2 py-2 max-w-xs">
                            {v.length === 0 ? (
                              <span className="text-navy-deep/70 text-xs">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {v.map((a: any) => {
                                  const isImg = (a.content_type ?? "").startsWith("image/");
                                  return (
                                    <a
                                      key={a.file_url}
                                      href={a.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={a.file_name ?? a.file_url}
                                      className="block border border-navy-deep/30 hover:border-red"
                                    >
                                      {isImg ? (
                                        <img src={a.file_url} alt={a.file_name ?? ""} className="h-12 w-12 object-cover" />
                                      ) : (
                                        <span className="block px-2 py-1 text-[10px] text-navy-deep">📎 {a.file_name ?? "file"}</span>
                                      )}
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        );
                      }
                      if (k === "notify_status") {
                        const status = String(v ?? "—");
                        const cls = status === "sent" ? "bg-green-700 text-white"
                          : status === "dlq" ? "bg-red text-white"
                          : status === "failed" ? "bg-amber-500 text-white"
                          : status === "pending" ? "bg-navy-deep text-white"
                          : "bg-navy-deep/10 text-navy-deep";
                        return (
                          <td key={k} className="px-2 py-2 whitespace-nowrap" title={r.notify_last_error ?? ""}>
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-display uppercase tracking-wider ${cls}`}>{status}</span>
                            {r.notify_attempts > 0 && <span className="ml-1 text-[10px] text-navy-deep/60">×{r.notify_attempts}</span>}
                          </td>
                        );
                      }
                      const display = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
                      return (
                        <td key={k} className="px-2 py-2 max-w-xs"
                            onDoubleClick={() => editable && setEditing({ id: r.id, field: k, value: display })}>
                          {isEditing && editing ? (
                            <div className="flex flex-col gap-1">
                              <label htmlFor={`edit-${r.id}-${k}`} className="sr-only">Edit {k}</label>
                              <textarea
                                id={`edit-${r.id}-${k}`}
                                value={editing.value}
                                onChange={(e) => setEditing({ id: editing.id, field: editing.field, value: e.target.value })}
                                aria-label={`Edit ${k}`}
                                className="border border-navy-deep px-1 py-0.5 text-xs w-full"
                                rows={3}
                              />
                              <div className="flex gap-1">
                                <button onClick={saveEdit} className="bg-red px-2 py-0.5 text-white text-xs">Save</button>
                                <button onClick={() => setEditing(null)} className="border border-navy-deep px-2 py-0.5 text-xs">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap break-words">{display.length > 200 ? display.slice(0,200) + "…" : display}</div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 whitespace-nowrap">
                      <button onClick={() => openHistory(r)} className="text-navy-deep hover:underline mr-2">History</button>
                      {tab === "customer_submissions" && (
                        <button onClick={() => retryNotification(r.id)} className="text-navy-deep hover:underline mr-2" title={r.notify_last_error ?? ""}>Retry email</button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-red hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-2 py-2 text-[11px] text-navy-deep/60">Double-click any cell to edit (except id, created_at, source, source_id, payload). Updates appear in realtime.</p>
          </div>
        )}
      </div>

      {historyFor && (
        <div role="dialog" aria-modal="true" aria-label="Submission history"
             className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={() => setHistoryFor(null)}>
          <div className="bg-white border-4 border-navy-deep max-w-3xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-navy-deep text-white px-4 py-3 flex items-center justify-between">
              <h2 className="font-display text-sm uppercase tracking-wider">History — {historyFor.label}</h2>
              <button onClick={() => setHistoryFor(null)} className="text-white hover:text-gold text-xl leading-none" aria-label="Close history">×</button>
            </div>
            <div className="p-4 text-sm">
              {historyLoading ? (
                <p className="text-navy-deep/70">Loading…</p>
              ) : historyRows.length === 0 ? (
                <p className="text-navy-deep/70">No audit entries yet. Future edits will be tracked here.</p>
              ) : (
                <ol className="space-y-3">
                  {historyRows.map((h) => (
                    <li key={h.id} className="border-l-4 border-gold pl-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-display uppercase tracking-wider text-white ${h.action === "insert" ? "bg-green-700" : h.action === "delete" ? "bg-red" : "bg-navy-deep"}`}>{h.action}</span>
                        <time className="text-xs text-navy-deep/70">{new Date(h.created_at).toLocaleString()}</time>
                        {h.changed_by && <span className="text-[11px] text-navy-deep/60">by {h.changed_by.slice(0,8)}…</span>}
                      </div>
                      {h.action === "update" && h.changed_fields && Object.keys(h.changed_fields).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {Object.entries(h.changed_fields as Record<string, { old: any; new: any }>).map(([field, diff]) => (
                            <li key={field} className="text-xs">
                              <strong className="text-navy-deep">{field}:</strong>{" "}
                              <span className="line-through text-red/70">{diff.old === null ? "∅" : typeof diff.old === "object" ? JSON.stringify(diff.old) : String(diff.old)}</span>
                              {" → "}
                              <span className="text-green-700">{diff.new === null ? "∅" : typeof diff.new === "object" ? JSON.stringify(diff.new) : String(diff.new)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
